import { useState, useCallback } from "react";
import { useStore, WA } from "../store/Store";

// EmailJS config
const EJS_PUBLIC_KEY  = "w52rNRsGD3ILH7TxK";
const EJS_SERVICE_ID  = "service_oaliqsl";
const EJS_TEMPLATE_ID = "77rvaqw";
// Admin WhatsApp number is now set in Admin Panel → Settings (dynamic, pulled from useStore)

// Razorpay
const RAZORPAY_KEY_ID = "rzp_live_THiFNJ2MlB7Fk0"; // your live Key ID (Key Secret lives only in Supabase secrets)

// Supabase Edge Function that creates the Razorpay order securely
const CREATE_ORDER_URL = "https://hmgeuxrzfvtmakzgpqub.supabase.co/functions/v1/razorpay-create-order";
const VERIFY_PAYMENT_URL = "https://hmgeuxrzfvtmakzgpqub.supabase.co/functions/v1/razorpay-verify-payment";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

async function sendOrderEmail(order) {
  try {
    const items = order.items?.map(i=>`${i.name} x${i.qty} = ₹${i.price*i.qty}`).join(", ");
    const addr  = order.addr;
    await fetch("https://api.emailjs.com/api/v1.0/email/send", {
      method:"POST",
      headers:{"Content-Type":"application/json"},
      body:JSON.stringify({
        service_id:  EJS_SERVICE_ID,
        template_id: EJS_TEMPLATE_ID,
        user_id:     EJS_PUBLIC_KEY,
        template_params:{
          order_id:       order.id,
          customer_name:  addr?.name||"Customer",
          customer_email: addr?.email||"",
          items:          items,
          total:          order.total?.toLocaleString("en-IN"),
          payment:        order.pay?.toUpperCase(),
          eta:            order.eta,
          address:        `${addr?.address}, ${addr?.city} - ${addr?.pincode}`,
        }
      })
    });
  } catch(e){ console.log("Email error:",e); }
}

function notifyAdminWhatsApp(order, adminNum) {
  const items = order.items?.map(i=>`• ${i.name} x${i.qty} = ₹${i.price*i.qty}`).join("\n");
  const msg = `🔔 *New Order — Matchwell*\n\nOrder: ${order.id}\nCustomer: ${order.addr?.name}\nPhone: ${order.addr?.phone}\nTotal: ₹${order.total}\nPayment: ${order.pay?.toUpperCase()}\n\nItems:\n${items}\n\nAddress: ${order.addr?.address}, ${order.addr?.city} - ${order.addr?.pincode}`;
  const url = `https://wa.me/${adminNum||"918766584266"}?text=${encodeURIComponent(msg)}`;
  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  a.rel = "noreferrer";
  a.click();
}

export default function CheckoutPage({ onNav }) {
  const { cart, cartTotal, recordServerOrder, notify, user, saveAddress, appliedCoupon, discount, freeShipThreshold, shippingFee } = useStore();
  const [step, setStep] = useState(1);
  const [addr, setAddr] = useState({ name:user?.name||"", phone:"", email:user?.email||"", address:"", city:"", state:"Maharashtra", pincode:"" });
  const [pay, setPay] = useState("upi");
  const [placed, setPlaced] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [saveThisAddr, setSaveThisAddr] = useState(false);
  const [pinLoading, setPinLoading] = useState(false);

  const lookupPincode = async (pincode) => {
    if (!/^\d{6}$/.test(pincode)) return;
    setPinLoading(true);
    try {
      const res = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
      const data = await res.json();
      const po = data?.[0]?.Status === "Success" ? data[0].PostOffice?.[0] : null;
      if (po) {
        setAddr(a => ({ ...a, city: po.District || a.city, state: po.State || a.state }));
      } else {
        notify("Pincode nahi mila — city/state manually likho", "err");
      }
    } catch (e) {
      console.log("Pincode lookup error:", e);
    } finally {
      setPinLoading(false);
    }
  };

  const afterDiscount = cartTotal - discount;
  const ship = afterDiscount >= freeShipThreshold ? 0 : shippingFee;
  const total = afterDiscount + ship;

  const finalizeOrder = (serverOrder) => {
    const o = recordServerOrder(serverOrder);
    setPlaced(o);
    if (addr.email) sendOrderEmail(o);
    // NOTE: we intentionally do NOT open WhatsApp here anymore — that was
    // hijacking the CUSTOMER's own device (wrong context) and interrupting
    // the confirmation screen on mobile. Admin sees new orders in the
    // Admin Panel's Orders tab instead.
  };

  const payWithRazorpay = useCallback(async () => {
    setProcessing(true);

    const loaded = await loadRazorpayScript();
    if (!loaded) {
      notify("Payment gateway load nahi hua. Internet check karke dobara try karein.", "err");
      setProcessing(false);
      return;
    }

    // Ask our Supabase Edge Function to create the order — it recomputes the
    // total server-side (including coupon discount) so the amount can't be
    // tampered with from the browser.
    let rzOrder;
    try {
      const res = await fetch(CREATE_ORDER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          cart: cart.map(i => ({ id: i.id, qty: i.qty, ss: i.ss })),
          couponCode: appliedCoupon || null,
        }),
      });
      rzOrder = await res.json();
      if (!res.ok || !rzOrder.id) throw new Error(rzOrder.error || "Order creation failed");
    } catch (e) {
      notify(e.message && e.message !== "Order creation failed" ? e.message : "Order create nahi ho paya. Dobara try karein.", "err");
      setProcessing(false);
      return;
    }

    const options = {
      key: RAZORPAY_KEY_ID,
      order_id: rzOrder.id,
      amount: rzOrder.amount,
      currency: rzOrder.currency,
      name: "Matchwell",
      description: `Order payment · ${cart.length} item${cart.length>1?"s":""}`,
      method: pay === "upi" ? { upi: true } : pay === "netbanking" ? { netbanking: true } : { card: true },
      prefill: { name: addr.name, contact: addr.phone, email: addr.email || "" },
      theme: { color: "#EC4899" },
      handler: async function (response) {
        let verifyData;
        try {
          const verifyRes = await fetch(VERIFY_PAYMENT_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              cart: cart.map(i => ({ id: i.id, qty: i.qty, sc: i.sc, ss: i.ss })),
              addr, pay, couponCode: appliedCoupon || null,
            }),
          });
          verifyData = await verifyRes.json();
          if (!verifyRes.ok || !verifyData.verified) {
            setProcessing(false);
            notify("Payment verify nahi ho paya. Agar paisa kata hai to WhatsApp par turant contact karein.", "err");
            return;
          }
        } catch (e) {
          setProcessing(false);
          notify("Payment verify nahi ho paya. Agar paisa kata hai to WhatsApp par turant contact karein.", "err");
          return;
        }
        setProcessing(false);
        finalizeOrder(verifyData.order);
      },
      modal: { ondismiss: () => setProcessing(false) },
    };

    const rzp = new window.Razorpay(options);
    rzp.on("payment.failed", () => {
      setProcessing(false);
      notify("Payment fail ho gaya. Dobara try karein.", "err");
    });
    rzp.open();
  }, [pay, cart, addr, appliedCoupon]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!cart.length && !placed) return (
    <div className="page-top-pad" style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:"80vh", background:"white", flexDirection:"column", gap:16 }}>
      <div style={{ fontSize:48 }}>🛒</div>
      <button onClick={()=>onNav("shop")} style={{ background:"#EC4899", color:"white", border:"none", padding:"13px 32px", borderRadius:50, fontSize:14, fontWeight:700, cursor:"pointer" }}>Shop Now</button>
    </div>
  );

  const handlePlace = () => {
    if (pay==="whatsapp") {
      window.open(`https://wa.me/${WA}?text=${encodeURIComponent(`*New Order — Matchwell*\nName: ${addr.name}\nPhone: ${addr.phone}\nAddress: ${addr.address}, ${addr.city} - ${addr.pincode}\n\nItems:\n${cart.map(i=>`• ${i.name} ×${i.qty} = ₹${i.price*i.qty}`).join("\n")}\n\nTotal: ₹${total}`)}`,"_blank");
      return;
    }
    payWithRazorpay();
  };

  const goStep2 = () => {
    if(!addr.name||!addr.phone||!addr.address||!addr.city||!addr.pincode){notify("Fill all address fields","err");return;}
    if(addr.phone.length!==10){notify("Valid 10-digit mobile number likho","err");return;}
    if (user && saveThisAddr) saveAddress({ label:"Home", ...addr });
    setStep(2);
  };

  if (placed) return (
    <div className="page-top-pad" style={{ minHeight:"100vh", background:"white", display:"flex", alignItems:"center", justifyContent:"center", padding:"80px 20px", flexDirection:"column" }}>
      <div style={{ background:"white", borderRadius:24, padding:"clamp(28px,5vw,48px)", maxWidth:480, width:"100%", boxShadow:"0 8px 48px rgba(0,0,0,0.08)", textAlign:"center" }}>
        <div style={{ fontSize:56, marginBottom:14 }}>🎉</div>
        <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:26, fontWeight:900, color:"#1a1a2e", marginBottom:8 }}>Order Placed!</h2>
        <p style={{ fontSize:13, color:"#999", marginBottom:20 }}>Thank you! Your order is confirmed.</p>
        <div style={{ background:"#fafafa", borderRadius:14, padding:16, marginBottom:20, textAlign:"left" }}>
          {[["Order ID",placed.id],...(placed.discount>0?[["Coupon",`${placed.couponCode} (-₹${placed.discount})`]]:[]),["Total",`₹${placed.total?.toLocaleString("en-IN")}`],["Payment",placed.pay?.toUpperCase()],["Expected Delivery",placed.eta]].map(([k,v])=>(
            <div key={k} style={{ display:"flex", justifyContent:"space-between", fontSize:13, marginBottom:7 }}>
              <span style={{ color:"#999" }}>{k}</span><span style={{ fontWeight:700 }}>{v}</span>
            </div>
          ))}
        </div>
        <div style={{ display:"flex", gap:10 }}>
          <button onClick={()=>onNav("orders")} style={{ flex:1, padding:"12px", background:"#1a1a2e", color:"white", border:"none", borderRadius:50, fontSize:12, fontWeight:700, cursor:"pointer" }}>Track Order</button>
          <button onClick={()=>onNav("home")} style={{ flex:1, padding:"12px", background:"white", color:"#1a1a2e", border:"1px solid #e5e5e5", borderRadius:50, fontSize:12, fontWeight:700, cursor:"pointer" }}>Home</button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="page-top-pad" style={{ background:"#fafafa", minHeight:"100vh" }}>
      <div style={{ background:"linear-gradient(135deg,#1a1a2e,#2d1b69)", padding:"20px clamp(14px,4vw,40px)" }}>
        <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:"clamp(20px,4vw,32px)", fontWeight:900, color:"white" }}>Secure Checkout 🔒</h1>
      </div>
      {/* Steps */}
      <div style={{ background:"white", padding:"12px clamp(14px,4vw,40px)", borderBottom:"1px solid #f0f0f0", display:"flex", gap:20, overflowX:"auto" }}>
        {["Address","Payment","Review"].map((st,i)=>(
          <div key={st} style={{ display:"flex", alignItems:"center", gap:8, cursor:i+1<step?"pointer":"default", flexShrink:0 }} onClick={()=>i+1<step&&setStep(i+1)}>
            <div style={{ width:24, height:24, borderRadius:"50%", background:step>i+1?"#10B981":step===i+1?"#EC4899":"#e5e5e5", color:"white", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:800 }}>{step>i+1?"✓":i+1}</div>
            <span style={{ fontSize:12, fontWeight:step===i+1?700:400, color:step===i+1?"#1a1a2e":"#999", textTransform:"uppercase", letterSpacing:1 }}>{st}</span>
          </div>
        ))}
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr clamp(240px,28%,300px)", gap:14, padding:"16px clamp(12px,3vw,28px)", alignItems:"start", maxWidth:1000, margin:"0 auto" }} className="checkout-grid">
        <div style={{ background:"white", borderRadius:16, padding:"clamp(18px,4vw,28px)", boxShadow:"0 2px 16px rgba(0,0,0,0.05)" }}>
          {/* Step 1 */}
          {step===1&&(
            <>
              <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700, color:"#1a1a2e", marginBottom:18 }}>Delivery Address</h3>

              {user?.addresses?.length > 0 && (
                <div style={{ marginBottom:18 }}>
                  <div style={{ fontSize:11, fontWeight:700, color:"#666", marginBottom:8, textTransform:"uppercase", letterSpacing:1 }}>Saved Addresses</div>
                  <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                    {user.addresses.map(a=>(
                      <div key={a.id} onClick={()=>setAddr({ name:a.name, phone:a.phone, address:a.address, city:a.city, state:a.state, pincode:a.pincode })}
                        style={{ border:"1.5px solid #e5e5e5", borderRadius:12, padding:"10px 14px", cursor:"pointer", fontSize:12 }}
                        onMouseEnter={e=>e.currentTarget.style.borderColor="#EC4899"}
                        onMouseLeave={e=>e.currentTarget.style.borderColor="#e5e5e5"}>
                        <span style={{ fontWeight:700, color:"#EC4899" }}>{a.label||"Address"}</span> — {a.name}, {a.address}, {a.city} - {a.pincode}
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize:11, color:"#999", marginTop:8 }}>Ya neeche naya address bharo:</div>
                </div>
              )}

              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                {[["name","Full Name *","1/3"],["phone","Phone *","1/3"],["email","Email (for order confirmation)","1/3"],["address","Street Address *","1/3"],["pincode","Pincode *","auto"],["city","City *","auto"],["state","State","auto"]].map(([k,pl,gc])=>(
                  <div key={k} style={{ gridColumn:gc }}>
                    <label style={{ fontSize:9, letterSpacing:1, textTransform:"uppercase", color:"#999", display:"block", marginBottom:4, fontWeight:700 }}>{pl}</label>
                    {k==="phone" ? (
                      <input value={addr.phone} type="tel" inputMode="numeric" maxLength={10}
                        onChange={e=>setAddr({...addr, phone:e.target.value.replace(/\D/g,"").slice(0,10)})}
                        placeholder="10-digit mobile number"
                        style={{ width:"100%", padding:"10px 14px", border:"1px solid #e5e5e5", borderRadius:12, fontSize:13, outline:"none", background:"#fafafa" }}
                        onFocus={e=>e.target.style.borderColor="#EC4899"}
                        onBlur={e=>e.target.style.borderColor="#e5e5e5"}/>
                    ) : k==="pincode" ? (
                      <>
                        <input value={addr.pincode} type="tel" inputMode="numeric" maxLength={6}
                          onChange={e=>{
                            const v = e.target.value.replace(/\D/g,"").slice(0,6);
                            setAddr({...addr, pincode:v});
                            if (v.length===6) lookupPincode(v);
                          }}
                          placeholder="6-digit pincode"
                          style={{ width:"100%", padding:"10px 14px", border:"1px solid #e5e5e5", borderRadius:12, fontSize:13, outline:"none", background:"#fafafa" }}
                          onFocus={e=>e.target.style.borderColor="#EC4899"}
                          onBlur={e=>e.target.style.borderColor="#e5e5e5"}/>
                        {pinLoading && <div style={{fontSize:10,color:"#999",marginTop:3}}>Finding city/state…</div>}
                      </>
                    ) : (
                      <input value={addr[k]} type={k==="email"?"email":"text"} onChange={e=>setAddr({...addr,[k]:e.target.value})} placeholder={k==="email"?"your@email.com (optional)":pl.replace(" *","")}
                        style={{ width:"100%", padding:"10px 14px", border:"1px solid #e5e5e5", borderRadius:12, fontSize:13, outline:"none", background:"#fafafa" }}
                        onFocus={e=>e.target.style.borderColor="#EC4899"}
                        onBlur={e=>e.target.style.borderColor="#e5e5e5"}/>
                    )}
                  </div>
                ))}
              </div>
              {user && (
                <label style={{ display:"flex", alignItems:"center", gap:8, marginTop:14, fontSize:12, color:"#666", cursor:"pointer" }}>
                  <input type="checkbox" checked={saveThisAddr} onChange={e=>setSaveThisAddr(e.target.checked)} style={{ accentColor:"#EC4899" }}/>
                  Save this address for next time
                </label>
              )}
              <button onClick={goStep2} style={{ marginTop:16, padding:"12px 30px", background:"#EC4899", color:"white", border:"none", borderRadius:50, fontSize:13, fontWeight:800, cursor:"pointer" }}>Continue →</button>
            </>
          )}

          {/* Step 2 */}
          {step===2&&(
            <>
              <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700, color:"#1a1a2e", marginBottom:18 }}>Payment Method</h3>
              {[{id:"upi",icon:"📱",l:"UPI",sub:"PhonePe, GPay, Paytm"},{id:"card",icon:"💳",l:"Credit / Debit Card",sub:"Visa, Mastercard, RuPay"},{id:"netbanking",icon:"🏦",l:"Net Banking",sub:"All major banks"},{id:"whatsapp",icon:"💬",l:"Order via WhatsApp",sub:"Place order directly"}].map(pm=>(
                <div key={pm.id} onClick={()=>setPay(pm.id)}
                  style={{ padding:14, border:`2px solid ${pay===pm.id?"#EC4899":"#f0f0f0"}`, background:pay===pm.id?"#FDF2F8":"white", cursor:"pointer", marginBottom:8, display:"flex", alignItems:"center", gap:12, borderRadius:14, transition:"all 0.2s" }}>
                  <div style={{ width:20, height:20, borderRadius:"50%", border:`2px solid ${pay===pm.id?"#EC4899":"#ccc"}`, display:"flex", alignItems:"center", justifyContent:"center" }}>
                    {pay===pm.id&&<span style={{ width:10, height:10, borderRadius:"50%", background:"#EC4899", display:"block" }}/>}
                  </div>
                  <span style={{ fontSize:18 }}>{pm.icon}</span>
                  <div><div style={{ fontWeight:700, fontSize:13 }}>{pm.l}</div><div style={{ fontSize:11, color:"#999" }}>{pm.sub}</div></div>
                </div>
              ))}
              {pay==="upi"&&(
                <div style={{ background:"#F0F9FF", border:"1px solid #BAE6FD", borderRadius:12, padding:"10px 14px", fontSize:11, color:"#0369A1", marginTop:6 }}>
                  Place Order dabate hi UPI apps ka QR / app-list khulega — Razorpay ke secure widget me.
                </div>
              )}
              <div style={{ display:"flex", gap:10, marginTop:14 }}>
                <button onClick={()=>setStep(1)} style={{ padding:"11px 22px", background:"white", color:"#1a1a2e", border:"1px solid #e5e5e5", borderRadius:50, fontSize:12, fontWeight:700, cursor:"pointer" }}>← Back</button>
                <button onClick={()=>setStep(3)} style={{ padding:"11px 26px", background:"#EC4899", color:"white", border:"none", borderRadius:50, fontSize:13, fontWeight:800, cursor:"pointer" }}>Review →</button>
              </div>
            </>
          )}

          {/* Step 3 */}
          {step===3&&(
            <>
              <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700, color:"#1a1a2e", marginBottom:14 }}>Review Order</h3>
              {cart.map(item=>(
                <div key={item.k} style={{ display:"flex", gap:10, padding:"10px 0", borderBottom:"1px solid #f5f5f5" }}>
                  <div style={{ width:44, height:50, background:"#f5f5f5", borderRadius:10, flexShrink:0, overflow:"hidden" }}>
                    {item.img?<img src={item.img} style={{width:"100%",height:"100%",objectFit:"cover",objectPosition:"top"}} alt=""/>:<div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>👗</div>}
                  </div>
                  <div style={{ flex:1 }}><div style={{ fontWeight:700, fontSize:13 }}>{item.name}</div><div style={{ fontSize:11, color:"#999" }}>{item.sc} · {item.ss} · ×{item.qty}</div></div>
                  <div style={{ fontWeight:800, color:"#1a1a2e", fontSize:13 }}>₹{(item.price*item.qty).toLocaleString("en-IN")}</div>
                </div>
              ))}
              <div style={{ background:"#fafafa", borderRadius:12, padding:12, margin:"12px 0", fontSize:12 }}>
                <div style={{ fontWeight:700, marginBottom:2 }}>{addr.name} · {addr.phone}</div>
                <div style={{ color:"#999" }}>{addr.address}, {addr.city} - {addr.pincode}</div>
                <div style={{ color:"#999", marginTop:2 }}>Payment: {pay.toUpperCase()}</div>
                {appliedCoupon && <div style={{ color:"#16A34A", marginTop:2, fontWeight:700 }}>Coupon: {appliedCoupon} (-₹{discount.toLocaleString("en-IN")})</div>}
              </div>
              <label style={{ display:"flex", alignItems:"flex-start", gap:8, marginBottom:14, fontSize:12, color:"#666", cursor:"pointer" }}>
                <input type="checkbox" checked={agreedTerms} onChange={e=>setAgreedTerms(e.target.checked)} style={{ accentColor:"#EC4899", marginTop:2 }}/>
                <span>
                  I agree to Matchwell's{" "}
                  <span onClick={e=>{e.preventDefault(); onNav("terms");}} style={{ color:"#EC4899", fontWeight:700, textDecoration:"underline" }}>Terms & Conditions</span>
                  {" "}and{" "}
                  <span onClick={e=>{e.preventDefault(); onNav("privacy");}} style={{ color:"#EC4899", fontWeight:700, textDecoration:"underline" }}>Privacy Policy</span>
                </span>
              </label>
              <div style={{ display:"flex", gap:10 }}>
                <button onClick={()=>setStep(2)} style={{ padding:"11px 22px", background:"white", color:"#1a1a2e", border:"1px solid #e5e5e5", borderRadius:50, fontSize:12, fontWeight:700, cursor:"pointer" }}>← Back</button>
                <button onClick={handlePlace} disabled={processing || !agreedTerms} style={{ flex:1, padding:"12px", background:pay==="whatsapp"?"#25D366":"#EC4899", color:"white", border:"none", borderRadius:50, fontSize:13, fontWeight:800, cursor:(processing||!agreedTerms)?"not-allowed":"pointer", opacity:(processing||!agreedTerms)?0.5:1 }}>
                  {processing ? "Opening secure payment…" : pay==="whatsapp"?"💬 Order on WhatsApp":`🔒 Place Order · ₹${total.toLocaleString("en-IN")}`}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Summary */}
        <div style={{ background:"white", borderRadius:16, padding:18, boxShadow:"0 4px 24px rgba(0,0,0,0.07)", position:"sticky", top:80 }}>
          <h4 style={{ fontWeight:800, fontSize:15, color:"#1a1a2e", marginBottom:12 }}>Summary</h4>
          {cart.map(i=>(
            <div key={i.k} style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:5 }}>
              <span style={{ color:"#666", flex:1, marginRight:6, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{i.name} ×{i.qty}</span>
              <span style={{ fontWeight:700, flexShrink:0 }}>₹{(i.price*i.qty).toLocaleString("en-IN")}</span>
            </div>
          ))}
          <div style={{ borderTop:"2px solid #f0f0f0", paddingTop:8, marginTop:8 }}>
            {discount > 0 && (
              <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:5, color:"#16A34A", fontWeight:700 }}>
                <span>Coupon ({appliedCoupon})</span><span>-₹{discount.toLocaleString("en-IN")}</span>
              </div>
            )}
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:5 }}><span style={{ color:"#999" }}>Shipping</span><span style={{ color:ship===0?"#16A34A":"#1a1a2e", fontWeight:ship===0?800:400 }}>{ship===0?"FREE":"₹"+ship}</span></div>
            <div style={{ display:"flex", justifyContent:"space-between", fontFamily:"'Playfair Display',serif", fontSize:18, fontWeight:900, color:"#1a1a2e", marginTop:6 }}>
              <span>Total</span><span>₹{total.toLocaleString("en-IN")}</span>
            </div>
          </div>
          <div style={{ background:"#F0FDF4", border:"1px solid #86EFAC", padding:"8px 10px", borderRadius:8, fontSize:11, color:"#16A34A", marginTop:10, fontWeight:700 }}>🔒 100% Secure</div>
        </div>
      </div>

    </div>
  );
}
